// Contrail V3.4.0 — colour picker: preset swatches, your saved custom colours, colours already used, and a free picker.
// A colour chosen with the free picker is remembered (this browser) and offered next time.
import { useMemo, useRef } from "react";
import { DISPLAY_COLOURS } from "../lib/icons";

const KEY = "fw.colours.custom";
const MAX = 12;
export function savedColours(): string[] {
  try { const v = JSON.parse(localStorage.getItem(KEY) ?? "[]"); return Array.isArray(v) ? v.filter((c) => /^#[0-9a-f]{6}$/i.test(c)).slice(0, MAX) : []; } catch { return []; }
}
export function rememberColour(c: string) {
  const up = c.toUpperCase();
  if (DISPLAY_COLOURS.includes(up)) return;
  try { localStorage.setItem(KEY, JSON.stringify([up, ...savedColours().filter((x) => x.toUpperCase() !== up)].slice(0, MAX))); } catch { /* storage blocked */ }
}

export default function ColourPicker({ value, onChange, used = [], allowAuto = false, autoLabel = "Auto" }: {
  value: string | null; onChange: (c: string | null) => void; used?: string[]; allowAuto?: boolean; autoLabel?: string;
}) {
  const custom = useMemo(() => {
    const preset = new Set(DISPLAY_COLOURS);
    const extra = [...savedColours(), ...used.map((c) => c.toUpperCase())].filter((c) => !preset.has(c.toUpperCase()));
    return [...new Set(extra.map((c) => c.toUpperCase()))].slice(0, MAX);
  }, [used, value]); // eslint-disable-line
  const input = useRef<HTMLInputElement>(null);
  const cur = value?.toUpperCase() ?? null;
  const sw = (c: string) => <button key={c} type="button" className={cur === c ? "on" : ""} style={{ background: c }} aria-label={c} title={c} onClick={() => onChange(c)} />;
  return (
    <div className="colour-picker">
      <div className="swatches" role="group" aria-label="Colours">
        {allowAuto && <button type="button" className={`auto ${value == null ? "on" : ""}`} onClick={() => onChange(null)}>{autoLabel}</button>}
        {DISPLAY_COLOURS.map(sw)}
        <button type="button" className={`pick ${cur && !DISPLAY_COLOURS.includes(cur) && !custom.includes(cur) ? "on" : ""}`}
          style={cur && !DISPLAY_COLOURS.includes(cur) ? { background: cur } : undefined}
          onClick={() => input.current?.click()} title="Pick any colour" aria-label="Pick any colour">+</button>
        <input ref={input} type="color" className="sr-only" value={value ?? "#257AF4"}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          onBlur={(e) => rememberColour(e.target.value)} />
      </div>
      {custom.length > 0 && (
        <div className="swatches custom" role="group" aria-label="Custom colours"><span className="sub">Custom</span>{custom.map(sw)}</div>
      )}
    </div>
  );
}
